import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
  BuildingStorefrontIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  TagIcon,
  DocumentTextIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { productService } from '../../services/productService'
import toast from 'react-hot-toast'

interface PriceProduct {
  product_id: number
  product_name: string
  sku: string
  brand: string
  category: string
  cost_price: number
  selling_price: number
  wholesale_price: number
  corporate_price: number
  stock_count: number
  latest_supplier: { company_name: string; supplier_code: string } | null
  last_purchase_date: string | null
  last_purchase_cost: number | null
}

interface ParsedLine {
  rawName: string
  price: number
  currency: string
}

interface MatchedItem {
  id: string
  rawName: string
  price: number
  finalPrice: number
  currency: string
  matchedProduct: PriceProduct | null
  currentPrice: number
  percentChange: number | null
}

// Simple fuzzy match score
function matchScore(a: string, b: string): number {
  const A = a.toLowerCase().replace(/[^a-z0-9]/g, '')
  const B = b.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (A === B) return 100
  if (B.includes(A)) return 90
  if (A.includes(B)) return 80
  // Count common substrings
  let common = 0
  for (let i = 0; i < Math.min(A.length, B.length); i++) {
    if (A[i] === B[i]) common++
  }
  return (common / Math.max(A.length, B.length)) * 70
}

function findBestMatch(name: string, products: PriceProduct[]): PriceProduct | null {
  let best: PriceProduct | null = null
  let bestScore = 0
  for (const p of products) {
    const score = matchScore(name, p.product_name)
    if (score > bestScore && score > 40) {
      bestScore = score
      best = p
    }
  }
  return best
}

function parsePastedText(text: string): ParsedLine[] {
  const lines = text.split('\n').filter(l => l.trim())
  const results: ParsedLine[] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    // Match patterns like "Product Name 3850$" or "Product Name $3850" or "Product Name 3850"
    const match = trimmed.match(/^(.*?)\s*[$€£¥]?(\d[\d,.]*)\s*[$€£¥]?$/)
    if (match) {
      const rawName = match[1].trim()
      const priceStr = match[2].replace(/,/g, '')
      const price = parseFloat(priceStr)
      const currency = trimmed.includes('$') ? 'USD' : trimmed.includes('€') ? 'EUR' : trimmed.includes('£') ? 'GBP' : 'USD'
      if (rawName && !isNaN(price) && price > 0) {
        results.push({ rawName, price, currency })
      }
    }
  }
  return results
}

export const StockPricesPage = () => {
  const [search, setSearch] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<any>({})

  // Paste supplier prices feature
  const [showPasteModal, setShowPasteModal] = useState(false)
  const [pastedText, setPastedText] = useState('')
  const [analyzedItems, setAnalyzedItems] = useState<MatchedItem[]>([])
  const [unmatchedItems, setUnmatchedItems] = useState<ParsedLine[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [productSearchForMatch, setProductSearchForMatch] = useState('')
  const [activeMatchDropdown, setActiveMatchDropdown] = useState<string | null>(null)

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['products-prices', search],
    queryFn: () => productService.getProductsWithPrices({ search }),
  })

  // Fetch all products for matching
  const { data: allProductsData } = useQuery({
    queryKey: ['products-all-for-matching'],
    queryFn: () => productService.getProductsWithPrices({}),
    enabled: showPasteModal,
  })

  const allProducts: PriceProduct[] = allProductsData?.data?.data || []
  const products: PriceProduct[] = data?.data?.data || []

  const updatePriceMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      productService.updatePrices(id, data),
    onSuccess: () => {
      toast.success('Price updated successfully')
      queryClient.invalidateQueries({ queryKey: ['products-prices'] })
      queryClient.invalidateQueries({ queryKey: ['products-all-for-matching'] })
      setEditingId(null)
    },
    onError: () => {
      toast.error('Failed to update price')
    },
  })

  const bulkUpdateMutation = useMutation({
    mutationFn: (data: { updates: any[]; reason: string }) =>
      productService.bulkUpdatePrices(data),
    onSuccess: () => {
      toast.success('Bulk prices updated successfully')
      queryClient.invalidateQueries({ queryKey: ['products-prices'] })
      queryClient.invalidateQueries({ queryKey: ['products-all-for-matching'] })
      setShowConfirmModal(false)
      setShowPasteModal(false)
      setPastedText('')
      setAnalyzedItems([])
      setUnmatchedItems([])
    },
    onError: () => {
      toast.error('Failed to update some prices')
    },
  })

  const handleEdit = (product: PriceProduct) => {
    setEditingId(product.product_id)
    setEditForm({
      cost_price: product.cost_price,
      selling_price: product.selling_price,
      wholesale_price: product.wholesale_price,
      corporate_price: product.corporate_price,
      reason: '',
    })
  }

  const handleSave = (id: number) => {
    if (!editForm.reason) {
      toast.error('Please provide a reason for the price change')
      return
    }
    updatePriceMutation.mutate({ id, data: editForm })
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditForm({})
  }

  const handleAnalyze = () => {
    const parsed = parsePastedText(pastedText)
    if (parsed.length === 0) {
      toast.error('No valid price data found. Format: Product Name 3850$')
      return
    }

    const matched: MatchedItem[] = []

    for (const line of parsed) {
      const bestMatch = findBestMatch(line.rawName, allProducts)
      if (bestMatch) {
        const currentPrice = bestMatch.selling_price || bestMatch.cost_price || 0
        const pctChange = currentPrice > 0
          ? ((line.price - currentPrice) / currentPrice) * 100
          : null
        matched.push({
          id: Math.random().toString(36).slice(2),
          rawName: line.rawName,
          price: line.price,
          finalPrice: line.price,
          currency: line.currency,
          matchedProduct: bestMatch,
          currentPrice,
          percentChange: pctChange,
        })
      } else {
        // Add unmatched items to the same list so they can be manually matched
        matched.push({
          id: Math.random().toString(36).slice(2),
          rawName: line.rawName,
          price: line.price,
          finalPrice: line.price,
          currency: line.currency,
          matchedProduct: null,
          currentPrice: 0,
          percentChange: null,
        })
      }
    }

    setAnalyzedItems(matched)
    setUnmatchedItems([])
    toast.success(`Auto-matched ${matched.filter(m => m.matchedProduct).length} of ${parsed.length} items`)
  }

  const handleChangeMatch = (itemId: string, product: PriceProduct) => {
    setAnalyzedItems(prev => prev.map(item => {
      if (item.id !== itemId) return item
      const currentPrice = product.selling_price || product.cost_price || 0
      const pctChange = currentPrice > 0
        ? ((item.finalPrice - currentPrice) / currentPrice) * 100
        : null
      return {
        ...item,
        matchedProduct: product,
        currentPrice,
        percentChange: pctChange,
      }
    }))
    setActiveMatchDropdown(null)
    setProductSearchForMatch('')
  }

  const updateFinalPrice = (itemId: string, newPrice: number) => {
    setAnalyzedItems(prev => prev.map(item => {
      if (item.id !== itemId) return item
      const pctChange = item.currentPrice > 0
        ? ((newPrice - item.currentPrice) / item.currentPrice) * 100
        : null
      return {
        ...item,
        finalPrice: Math.max(0, newPrice),
        percentChange: pctChange,
      }
    }))
  }

  const handleOpenConfirm = () => {
    if (analyzedItems.length === 0) {
      toast.error('Nothing to update. Please analyze first.')
      return
    }
    setShowConfirmModal(true)
  }

  const handleBulkUpdate = () => {
    const updates = analyzedItems
      .filter(item => item.matchedProduct)
      .map(item => ({
        product_id: item.matchedProduct!.product_id,
        selling_price: item.finalPrice,
        cost_price: item.matchedProduct!.cost_price,
        wholesale_price: item.matchedProduct!.wholesale_price,
        corporate_price: item.matchedProduct!.corporate_price,
      }))

    bulkUpdateMutation.mutate({
      updates,
      reason: 'Supplier price list update',
    })
  }

  const filteredProductsForMatch = useMemo(() => {
    if (!productSearchForMatch) return allProducts.slice(0, 20)
    const term = productSearchForMatch.toLowerCase()
    return allProducts
      .filter(p =>
        p.product_name.toLowerCase().includes(term) ||
        p.sku.toLowerCase().includes(term)
      )
      .slice(0, 20)
  }, [productSearchForMatch, allProducts])

  const formatPrice = (price: number) => {
    if (!price || price === 0) return '-'
    return `KSh ${price.toLocaleString()}`
  }

  const calculateMargin = (selling: number, cost: number) => {
    if (!cost || cost === 0) return '-'
    const margin = ((selling - cost) / selling) * 100
    return `${margin.toFixed(1)}%`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Prices</h1>
          <p className="text-sm text-gray-500 mt-1">
            View and manage product prices, margins, and suppliers
          </p>
        </div>
        <button
          onClick={() => setShowPasteModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <DocumentTextIcon className="w-5 h-5" />
          Paste Supplier Prices
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <TagIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-xl font-bold text-gray-900">{products.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Avg Margin</p>
              <p className="text-xl font-bold text-gray-900">
                {products.length > 0
                  ? `${(
                      products.reduce(
                        (sum, p) =>
                          sum +
                          (p.selling_price > 0
                            ? ((p.selling_price - p.cost_price) / p.selling_price) * 100
                            : 0),
                        0
                      ) / products.filter((p) => p.selling_price > 0).length
                    ).toFixed(1)}%`
                  : '-'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <BuildingStorefrontIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">With Suppliers</p>
              <p className="text-xl font-bold text-gray-900">
                {products.filter((p) => p.latest_supplier).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <TagIcon className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">In Stock</p>
              <p className="text-xl font-bold text-gray-900">
                {products.reduce((sum, p) => sum + p.stock_count, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, SKU, or barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FunnelIcon className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selling Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wholesale
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margin
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Latest Supplier
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Purchase
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    No products found
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.product_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <Link
                          to={`/products/${product.product_id}`}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600"
                        >
                          {product.product_name}
                        </Link>
                        <p className="text-xs text-gray-500">{product.sku}</p>
                        <p className="text-xs text-gray-400">
                          {product.brand} / {product.category}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">{product.stock_count}</span>
                    </td>
                    {editingId === product.product_id ? (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={editForm.cost_price}
                            onChange={(e) =>
                              setEditForm({ ...editForm, cost_price: parseFloat(e.target.value) || 0 })
                            }
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={editForm.selling_price}
                            onChange={(e) =>
                              setEditForm({ ...editForm, selling_price: parseFloat(e.target.value) || 0 })
                            }
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={editForm.wholesale_price}
                            onChange={(e) =>
                              setEditForm({ ...editForm, wholesale_price: parseFloat(e.target.value) || 0 })
                            }
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-2">-</td>
                        <td className="px-4 py-2" colSpan={2}>
                          <input
                            type="text"
                            placeholder="Reason for change"
                            value={editForm.reason}
                            onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleSave(product.product_id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Save"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Cancel"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatPrice(product.cost_price)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatPrice(product.selling_price)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatPrice(product.wholesale_price)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              product.selling_price > 0 && product.cost_price > 0
                                ? ((product.selling_price - product.cost_price) /
                                    product.selling_price) *
                                    100 >=
                                  30
                                  ? 'bg-green-100 text-green-800'
                                  : ((product.selling_price - product.cost_price) /
                                      product.selling_price) *
                                      100 >=
                                    15
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {calculateMargin(product.selling_price, product.cost_price)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {product.latest_supplier ? (
                            <div>
                              <p className="font-medium text-gray-900">
                                {product.latest_supplier.company_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {product.latest_supplier.supplier_code}
                              </p>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {product.last_purchase_date
                            ? new Date(product.last_purchase_date).toLocaleDateString()
                            : '-'}
                          {product.last_purchase_cost ? (
                            <p className="text-xs text-gray-500">
                              @ KSh {product.last_purchase_cost.toLocaleString()}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                            title="Edit prices"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paste Supplier Prices Modal */}
      {showPasteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowPasteModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">Paste Supplier Prices</h2>
                <button onClick={() => setShowPasteModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Paste Area */}
                {analyzedItems.length === 0 && unmatchedItems.length === 0 && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Paste supplier price list
                    </label>
                    <textarea
                      value={pastedText}
                      onChange={(e) => setPastedText(e.target.value)}
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                      placeholder={`1DXmkii 3850$
5D body 1250$
5D kit 2100$
2000D DC 300$
...`}
                    />
                    <p className="text-xs text-gray-500">
                      Format: Product Name followed by price with $ sign. One item per line.
                    </p>
                    <button
                      type="button"
                      onClick={handleAnalyze}
                      disabled={!pastedText.trim()}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      Analyze
                    </button>
                  </div>
                )}

                {/* Analysis Results */}
                {(analyzedItems.length > 0 || unmatchedItems.length > 0) && (
                  <div className="space-y-6">
                    {/* Analysis Results Table */}
                    {analyzedItems.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">
                          Analysis Results ({analyzedItems.length})
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            ({analyzedItems.filter(i => i.matchedProduct).length} matched, {analyzedItems.filter(i => !i.matchedProduct).length} unmatched)
                          </span>
                        </h3>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium">Extracted Name</th>
                                  <th className="px-3 py-2 text-left font-medium">Product</th>
                                  <th className="px-3 py-2 text-right font-medium">Current Price</th>
                                  <th className="px-3 py-2 text-right font-medium">Pasted Price</th>
                                  <th className="px-3 py-2 text-right font-medium">Final Selling Price</th>
                                  <th className="px-3 py-2 text-right font-medium">% Change</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {[...analyzedItems]
                                  .sort((a, b) => Number(!!b.matchedProduct) - Number(!!a.matchedProduct))
                                  .map((item) => (
                                  <tr
                                    key={item.id}
                                    className={`hover:bg-gray-50 ${!item.matchedProduct ? 'bg-yellow-50/50' : ''}`}
                                  >
                                    <td className="px-3 py-2 text-gray-700">
                                      {item.rawName}
                                      {!item.matchedProduct && (
                                        <span className="ml-1 text-xs text-yellow-600 font-medium">(unmatched)</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 relative">
                                      <div className="relative">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setActiveMatchDropdown(activeMatchDropdown === item.id ? null : item.id)
                                            setProductSearchForMatch('')
                                          }}
                                          className="flex items-center gap-1 text-left"
                                        >
                                          {item.matchedProduct ? (
                                            <>
                                              <span className="font-medium text-primary-600 hover:underline">
                                                {item.matchedProduct.product_name}
                                              </span>
                                              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                                            </>
                                          ) : (
                                            <>
                                              <span className="font-medium text-yellow-600 hover:underline">
                                                Select product...
                                              </span>
                                              <ChevronDownIcon className="w-3 h-3 text-yellow-600" />
                                            </>
                                          )}
                                        </button>
                                        {item.matchedProduct && (
                                          <p className="text-xs text-gray-500">{item.matchedProduct.sku}</p>
                                        )}

                                        {activeMatchDropdown === item.id && (
                                          <div className="absolute z-30 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                                            <div className="p-2 border-b sticky top-0 bg-white">
                                              <input
                                                type="text"
                                                placeholder="Search products..."
                                                value={productSearchForMatch}
                                                onChange={(e) => setProductSearchForMatch(e.target.value)}
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </div>
                                            {filteredProductsForMatch.length === 0 ? (
                                              <p className="px-3 py-2 text-xs text-gray-500">No products found</p>
                                            ) : (
                                              filteredProductsForMatch.map((p) => (
                                                <button
                                                  key={p.product_id}
                                                  type="button"
                                                  onClick={() => handleChangeMatch(item.id, p)}
                                                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                                                >
                                                  <p className="text-sm font-medium">{p.product_name}</p>
                                                  <p className="text-xs text-gray-500">
                                                    {p.sku} · Current: KSh {p.selling_price?.toLocaleString() || 0}
                                                  </p>
                                                </button>
                                              ))
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-600">
                                      {item.currentPrice > 0 ? `KSh ${item.currentPrice.toLocaleString()}` : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold text-gray-900">
                                      {item.price.toLocaleString()} {item.currency}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={item.finalPrice}
                                        onChange={(e) => updateFinalPrice(item.id, parseFloat(e.target.value) || 0)}
                                        className="w-24 text-right text-sm border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-primary-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      {typeof item.percentChange === 'number' ? (
                                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                          item.percentChange > 0
                                            ? 'bg-green-100 text-green-700'
                                            : item.percentChange < 0
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-gray-100 text-gray-600'
                                        }`}>
                                          {item.percentChange > 0 ? '+' : ''}{item.percentChange.toFixed(1)}%
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => {
                          setAnalyzedItems([])
                          setUnmatchedItems([])
                          setPastedText('')
                        }}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                      >
                        Paste Again
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenConfirm}
                        disabled={analyzedItems.length === 0}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                      >
                        Update {analyzedItems.filter(i => i.matchedProduct).length} Prices
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowConfirmModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Confirm Price Updates</h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Products to update</span>
                  <span className="font-medium">{analyzedItems.filter(i => i.matchedProduct).length}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Unmatched (skipped)</span>
                  <span className="font-medium">{analyzedItems.filter(i => !i.matchedProduct).length}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Price increases</span>
                  <span className="font-medium text-green-600">
                    {analyzedItems.filter(i => (i.percentChange || 0) > 0).length}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Price decreases</span>
                  <span className="font-medium text-red-600">
                    {analyzedItems.filter(i => (i.percentChange || 0) < 0).length}
                  </span>
                </div>
              </div>

              <div className="mt-4 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-right">Old</th>
                      <th className="px-3 py-2 text-right">New</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {analyzedItems.filter(i => i.matchedProduct).map((item) => (
                      <tr key={item.id}>
                        <td className="px-3 py-2 truncate max-w-[200px]">{item.matchedProduct!.product_name}</td>
                        <td className="px-3 py-2 text-right text-gray-500">
                          KSh {item.currentPrice.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {item.finalPrice.toLocaleString()} {item.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpdate}
                  disabled={bulkUpdateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {bulkUpdateMutation.isPending ? 'Updating...' : 'Confirm Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
